import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Mail, Loader2, ArrowLeft, RefreshCw, CheckCircle, HelpCircle } from 'lucide-react';
import { sendEmailVerification, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useLang } from '../i18n';
import './AuthScreen.css';
import './EmailVerificationGate.css';

export default function EmailVerificationGate({ user }) {
  const { t } = useLang();
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('error'); // 'error' | 'success'

  const startChecking = useCallback(async () => {
    if (document.hidden) return;
    setLoadingCheck(true);
    try {
      await user.reload();
      if (user.emailVerified) {
        setMessage("E-mail verificado com sucesso! Entrando no app...");
        setMessageType("success");
        setTimeout(() => {
          window.location.reload();
        }, 1200);
      }
    } catch (e) {
      console.warn('Auto check reload error:', e);
    } finally {
      setLoadingCheck(false);
    }
  }, [user]);

  // Periodic check when tab gains focus
  useEffect(() => {
    startChecking();
    const handleVisibilityChange = () => {
      if (!document.hidden && !user.emailVerified) startChecking();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [startChecking, user.emailVerified]);

  // Cooldown countdown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const interval = setInterval(() => {
      setCooldown(prev => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown]);

  const handleCheckVerification = async () => {
    if (loadingCheck || loadingResend) return;
    setLoadingCheck(true);
    setMessage(null);
    try {
      await user.reload();
      if (user.emailVerified) {
        setMessage("E-mail verificado com sucesso! Entrando no app...");
        setMessageType("success");
        setTimeout(() => {
          window.location.reload();
        }, 1200);
      } else {
        setMessage("Ainda não encontramos a confirmação. Aguarde alguns segundos ou confira se clicou no link correto.");
        setMessageType("error");
      }
    } catch (e) {
      console.error('Reload user error:', e);
      setMessage("Erro ao conectar com o servidor. Tente novamente.");
      setMessageType("error");
    } finally {
      setLoadingCheck(false);
    }
  };

  const handleResendEmail = async () => {
    if (loadingCheck || loadingResend || cooldown > 0) return;
    setLoadingResend(true);
    setMessage(null);
    try {
      // Refresh user session token to prevent stale token/session errors
      await user.reload();
      await sendEmailVerification(user);
      setMessage(t('email_verify_resent'));
      setMessageType("success");
      setCooldown(60); // 60 seconds cooldown to prevent spam
    } catch (e) {
      console.error('Resend verification error:', e);
      if (e?.code === 'auth/too-many-requests') {
        setMessage("Muitas tentativas de reenvio. Aguarde um momento antes de tentar novamente.");
      } else if (e?.message?.includes('-26') || e?.code?.includes('-26')) {
        setMessage("Erro interno do Firebase (Código -26). Verifique se o serviço de envio de e-mails está ativo, se a cota diária de e-mails foi excedida ou se há erro nas configurações de SMTP no console do Firebase.");
      } else {
        setMessage("Não foi possível reenviar agora. Tente novamente em alguns segundos.");
      }
      setMessageType("error");
    } finally {
      setLoadingResend(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.error('Sign out error:', e);
    }
  };

  return (
    <div className="mi-auth-premium min-h-screen w-full flex items-center justify-center p-4 relative overflow-y-auto">
      {/* Premium Floating Particles background */}
      <div className="mi-auth-particles">
        <div className="mi-auth-particle" style={{ left: '10%', width: '8px', height: '8px', animationDuration: '6s', animationDelay: '0s' }} />
        <div className="mi-auth-particle" style={{ left: '30%', width: '12px', height: '12px', animationDuration: '8s', animationDelay: '2s' }} />
        <div className="mi-auth-particle" style={{ left: '70%', width: '6px', height: '6px', animationDuration: '5s', animationDelay: '1s' }} />
        <div className="mi-auth-particle" style={{ left: '95%', width: '10px', height: '10px', animationDuration: '7s', animationDelay: '3s' }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="mi-auth-premium__form-card w-full max-w-[480px] p-6 sm:p-10 flex flex-col gap-6 relative z-10"
      >
        {/* Top Header Badge & Titles */}
        <div className="flex flex-col items-center text-center gap-3">
          <div className="mi-auth-premium__form-badge flex items-center justify-center">
            <Mail size={16} className="text-[#CFF008]" />
          </div>
          <h2 className="text-xl sm:text-2xl font-black uppercase tracking-tighter text-[#F4F7FB] m-0">
            {t('email_verify_title')}
          </h2>
          <p className="text-xs sm:text-sm text-[#B6C0CC] font-medium m-0 leading-relaxed max-w-sm">
            {t('email_verify_subtitle')}
          </p>
        </div>

        {/* Email Box Highlight */}
        <div className="border border-white/5 bg-white/[0.02] rounded-xl p-4 flex flex-col gap-1.5 text-center">
          <span className="text-[10px] uppercase tracking-widest font-black text-white/40">
            {t('email_verify_sent_to')}
          </span>
          <strong className="text-sm sm:text-base font-bold text-[#CFF008] break-all">
            {user?.email}
          </strong>
        </div>

        {/* Instructions list */}
        <div className="flex flex-col gap-3 text-xs sm:text-sm text-[#B6C0CC]/80">
          <p className="text-center m-0 leading-relaxed">
            {t('email_verify_instruction')}
          </p>
          
          <div className="bg-black/20 rounded-xl p-4 space-y-2.5 border border-white/5">
            <div className="flex items-center gap-3">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#CFF008]/10 text-[#CFF008] border border-[#CFF008]/20 flex items-center justify-center text-[10px] font-bold">1</span>
              <span className="font-medium text-white/95">Abra sua caixa de entrada</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#CFF008]/10 text-[#CFF008] border border-[#CFF008]/20 flex items-center justify-center text-[10px] font-bold">2</span>
              <span className="font-medium text-white/95">Procure pelo link enviado</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#CFF008]/10 text-[#CFF008] border border-[#CFF008]/20 flex items-center justify-center text-[10px] font-bold">3</span>
              <span className="font-medium text-white/95">Confirme para liberar o acesso</span>
            </div>
          </div>
        </div>

        {/* Notification Feedback Messages */}
        {message && (
          <div className={`p-3 sm:p-4 rounded-xl text-xs sm:text-sm font-bold text-center border transition-all ${
            messageType === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
              : 'bg-red-500/10 border-red-500/20 text-red-400'
          }`}>
            {message}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-3">
          {/* Main: Already Verified */}
          <button
            onClick={handleCheckVerification}
            disabled={loadingCheck || loadingResend}
            className="mi-auth-premium__btn-primary w-full min-h-[48px] uppercase tracking-widest font-black text-xs sm:text-sm transition-all"
          >
            {loadingCheck ? (
              <span className="spinner-wrap">
                <Loader2 size={16} className="animate-spin text-black" />
                <span>Verificando...</span>
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <CheckCircle size={15} />
                {t('email_verify_cta')}
              </span>
            )}
          </button>

          {/* Secondary: Resend Link */}
          <button
            onClick={handleResendEmail}
            disabled={loadingCheck || loadingResend || cooldown > 0}
            className="mi-auth-premium__btn-google w-full min-h-[48px] uppercase tracking-widest font-black text-xs sm:text-sm transition-all"
          >
            {loadingResend ? (
              <span className="spinner-wrap">
                <Loader2 size={16} className="animate-spin text-white" />
                <span>Enviando...</span>
              </span>
            ) : cooldown > 0 ? (
              <span>Reenviar em {cooldown}s</span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <RefreshCw size={13} />
                {t('email_verify_resend')}
              </span>
            )}
          </button>

          {/* Tertiary: Back to Login */}
          <button
            onClick={handleSignOut}
            className="text-white/40 hover:text-white/70 text-xs sm:text-sm font-bold flex items-center justify-center gap-2 mt-2 transition-colors border-none bg-transparent cursor-pointer"
          >
            <ArrowLeft size={13} />
            <span>{t('email_verify_back_login')}</span>
          </button>
        </div>

        {/* Bottom Help Footer */}
        <div className="flex items-center justify-center gap-1.5 text-[10px] sm:text-xs text-white/20 mt-2 font-medium">
          <HelpCircle size={12} />
          <span>Precisa de ajuda? Suporte via Discord ou Telegram</span>
        </div>
      </motion.div>
    </div>
  );
}
